import { useState, useEffect, useCallback } from 'react';
import {
  getQueuedDrafts,
  getQueueCount,
  removeQueuedDraft,
  updateQueuedDraft,
  queueOfflineDraft,
  OFFLINE_QUEUE_EVENT,
} from '../utils/offlineStore';
import { useConnectionStatus } from './useConnectionStatus';

/**
 * useOfflineQueue
 * ----------------
 * Tracks the offline draft queue and exposes a `drainQueue` operation that
 * attempts to POST queued drafts to `/api/history/save-draft` (or any caller-
 * supplied endpoint) once connectivity returns.
 *
 * Returns:
 *   queue       – array of queued drafts, newest first
 *   queueCount  – number of pending drafts (cheap to read)
 *   refresh     – manual reload
 *   enqueue     – add a snapshot to the queue (returns draft id)
 *   drainQueue  – attempt to sync all pending drafts via supplied `sender`
 *                 function (sender returns true on success, false to retry)
 */
export const useOfflineQueue = () => {
  const { status, online } = useConnectionStatus();
  const [queue, setQueue] = useState([]);
  const [queueCount, setQueueCount] = useState(0);

  const refresh = useCallback(async () => {
    const [items, count] = await Promise.all([getQueuedDrafts(), getQueueCount()]);
    setQueue(items);
    setQueueCount(count);
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener(OFFLINE_QUEUE_EVENT, handler);
    return () => window.removeEventListener(OFFLINE_QUEUE_EVENT, handler);
  }, [refresh]);

  const enqueue = useCallback(async (snapshot) => {
    const id = await queueOfflineDraft(snapshot);
    return id;
  }, []);

  const drainQueue = useCallback(
    async (sender) => {
      if (status === 'offline' || !online) return { drained: 0, failed: 0 };
      const items = await getQueuedDrafts();
      let drained = 0;
      let failed = 0;
      for (const item of items) {
        await updateQueuedDraft(item.id, { status: 'syncing' });
        try {
          const ok = await sender(item);
          if (ok) {
            await removeQueuedDraft(item.id);
            drained += 1;
          } else {
            await updateQueuedDraft(item.id, { status: 'failed' });
            failed += 1;
          }
        } catch (e) {
          await updateQueuedDraft(item.id, { status: 'failed', last_error: String(e?.message || e) });
          failed += 1;
        }
      }
      await refresh();
      return { drained, failed };
    },
    [status, online, refresh]
  );

  return { queue, queueCount, refresh, enqueue, drainQueue };
};

export default useOfflineQueue;
